<template>
  <div class="h-full overflow-scroll p-4">
    <div class="w-full max-w-screen-2xl mx-auto flex flex-col gap-4">
      <div class="flex flex-col lg:flex-row gap-4">
        <WeekNavigator
          :current-week="weekNavigatorData"
          :curriculum="curriculumData"
          :expedition-id="expeditionsStore.latestExpeditionId"
          @week-changed="handleWeekChange"
        />
        <div class="flex flex-row gap-4 w-full flex-wrap">
          <BacklogStats />
          <ProgressBar/>
          <TopPerformers :expedition-id="expeditionsStore.latestExpeditionId" class="ml-auto" />
        </div>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center h-32">
        <LoaderCircle class="w-6 h-6 animate-spin" />
      </div>

      <template v-else>
        <div class="mb-8">
          <h2 class="text-lg font-medium mb-4">Week {{ activeWeek }}</h2>
          <div class="bg-white rounded-lg shadow-sm">
            <DemoCurriculumTable
              :items="currentWeekData"
              :is-loading="false"
              :is-notes-loading="isNotesLoading"
              :is-updating="isUpdating"
              :personal-notes="personalNotes"
              @toggle-expand="handleToggleExpand"
              @save-notes="handleSaveNotes"
              @update-status="handleStatusUpdate"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import WeekNavigator from '@/components/dashboard/WeekNavigator.vue'
import DemoCurriculumTable from '@/components/dashboard/DemoCurriculumTable.vue'
import BacklogStats from '@/components/dashboard/BacklogStats.vue'
import TopPerformers from '@/components/dashboard/TopPerformers.vue'
import ProgressBar from '@/components/dashboard/ProgressBar.vue'
import { LoaderCircle } from 'lucide-vue-next'
import { useCurriculumStore } from '@/stores/curriculum'
import { useExpeditionsStore } from '@/stores/expeditions'
import { useUserStore } from '@/stores/user'
import { useToast } from 'primevue/usetoast'

const curriculumStore = useCurriculumStore()
const expeditionsStore = useExpeditionsStore()
const userStore = useUserStore()
const toast = useToast()

const route = useRoute()
const router = useRouter()

const isLoading = ref(false)
const isNotesLoading = ref(false)
const isUpdating = ref(false)
const personalNotes = ref({})

// Initialize active week from route or default to 1
const activeWeek = ref(parseInt(route.query.week as string) || 1)

// Create demo data structure for curriculum
const initializeDemoData = () => {
  // Create sample tasks for 4 weeks
  const demoData: any = {
    path_steps: {}
  }
  
  for (let week = 1; week <= 4; week++) {
    demoData.path_steps[week] = Array.from({ length: 5 }, (_, index) => ({
      id: week * 100 + index,
      status: 'TO START',
      task: {
        id: week * 100 + index,
        name: `Demo Task ${index + 1} for Week ${week}`,
        url: `https://example.com/task-${week}-${index + 1}`,
        details: `This is a demo task ${index + 1} for week ${week}`,
        section: {
          id: 1,
          name: 'Demo Section'
        }
      },
      week_order: week,
      order_within_week: index + 1,
      is_required: true,
      points: 10
    }))
  }
  
  return demoData
}

// Create a computed property for the current week data structure
const currentWeekData = computed(() => {
  const weekData = curriculumStore.expeditionSteps?.path_steps[activeWeek.value] || []
  return weekData.map((step: any) => ({
    id: step.id,
    status: step.status || 'TO START',
    adventurer: userStore.currentUser?.id || 0,
    path_step: {
      id: step.id,
      task: {
        id: step.task.id,
        name: step.task.name,
        url: step.task.url,
        details: step.task.details,
        section: step.task.section
      },
      week_order: step.week_order,
      order_within_week: step.order_within_week,
      is_required: step.is_required,
      points: step.points
    },
    expedition: expeditionsStore.latestExpeditionId || 0,
    finished_datetime: null,
    created_at: null,
    updated_at: null,
    week_order: step.week_order
  }))
})

// Create a computed property for the WeekNavigator's currentWeek prop
const weekNavigatorData = computed(() => ({
  current_week: activeWeek.value,
  preview_weeks: 4 // Allow viewing up to 4 weeks
}))

// Create a computed property for the curriculum data
const curriculumData = computed(() => ({
  backlog: {},
  future: {},
  // Add any other required properties for the curriculum object
}))

// Watch for route changes
watch(
  () => route.query.week,
  (newWeek) => {
    if (newWeek) {
      activeWeek.value = parseInt(newWeek as string)
    }
  }
)

const handleWeekChange = (weekNumber: number) => {
  activeWeek.value = weekNumber
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace({ query: { ...route.query, week: weekNumber.toString() } })
}

const handleToggleExpand = () => {
  // No-op in demo mode
}

const handleSaveNotes = () => {
  // No-op in demo mode
}

const handleStatusUpdate = (payload: { id: number; status: string }) => {
  // Find the week that contains the item
  const weekData = curriculumStore.expeditionSteps?.path_steps[activeWeek.value] || []
  const stepToUpdate = weekData.find((step: any) => step.id === payload.id)
  if (stepToUpdate) {
    stepToUpdate.status = payload.status
  }
}

onMounted(() => {
  // Initialize demo data
  curriculumStore.expeditionSteps = initializeDemoData()
  
  // Set initial week from route or default to 1
  const weekFromQuery = route.query.week
  if (weekFromQuery) {
    activeWeek.value = parseInt(weekFromQuery as string)
  } else {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({ query: { ...route.query, week: '1' } })
  }
})
</script>